export interface EventLinkPlayerList {
  eventName: string;
  eventDate: string;
  playerNames: string[];
}

export interface AttendanceUser {
  id: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
}

export interface AttendanceMatch {
  importedName: string;
  users: AttendanceUser[];
  status: "matched" | "ambiguous" | "unmatched";
}

export function normalizeAttendanceName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function parseEventLinkPlayerList(
  report: string,
): EventLinkPlayerList | null {
  const lines = report.split(/\r?\n/);
  const reportIndex = lines.findIndex((line) =>
    /Report:\s*Player List/i.test(line),
  );
  const playerHeaderIndex = lines.findIndex((line) =>
    /^\s*Player\s+Table\s*$/i.test(line),
  );
  const separatorIndex = lines.findIndex(
    (line, index) => index > playerHeaderIndex && /^\s*-{5,}\s*$/.test(line),
  );

  if (reportIndex < 0 || playerHeaderIndex < 0 || separatorIndex < 0) {
    return null;
  }

  const tableColumn = lines[playerHeaderIndex].search(/\bTable\b/i);
  const playerNames: string[] = [];
  for (const line of lines.slice(separatorIndex + 1)) {
    if (/^\s*EventLink\s*-\s*Copyright/i.test(line)) break;
    const name = (tableColumn > 0 ? line.slice(0, tableColumn) : line).trim();
    if (name) playerNames.push(name);
  }

  if (playerNames.length === 0) return null;

  return {
    eventName:
      lines
        .find((line) => /^\s*Event:/i.test(line))
        ?.replace(/^\s*Event:\s*/i, "")
        .replace(/\s+\(\d+\)\s*$/, "")
        .trim() || "Imported Event",
    eventDate:
      lines
        .find((line) => /^\s*Event Date:/i.test(line))
        ?.replace(/^\s*Event Date:\s*/i, "")
        .trim() || "",
    playerNames: Array.from(new Set(playerNames)),
  };
}

export function matchAttendancePlayers(
  importedNames: string[],
  users: AttendanceUser[],
): AttendanceMatch[] {
  return importedNames.map((importedName) => {
    const truncated = /\.{3}$/.test(importedName.trim());
    const normalizedImportedName = normalizeAttendanceName(
      importedName.replace(/\.{3}$/, ""),
    );
    const matches = users.filter((user) => {
      const names = [
        user.displayName,
        [user.firstName, user.lastName].filter(Boolean).join(" "),
        user.username,
      ]
        .filter(Boolean)
        .map((name) => normalizeAttendanceName(String(name)));

      return names.some((name) =>
        truncated
          ? name.startsWith(normalizedImportedName)
          : name === normalizedImportedName,
      );
    });

    return {
      importedName,
      users: matches,
      status:
        matches.length === 1
          ? "matched"
          : matches.length > 1
            ? "ambiguous"
            : "unmatched",
    };
  });
}
